import { useEffect } from 'react';

// Plays a muted, looping clip while it is on screen and pauses it when it
// scrolls away, so a page of clips is not decoding video nobody can see. A
// clip that is not displayed at all (a gallery tile behind "Show all") never
// intersects, so it never starts.
//
// Visitors who ask for reduced motion get no autoplay: the poster stays until
// they press play themselves.
//
// `enabled` lets a player that is sometimes a sit-down video (ProjectVideo's
// guide mode) opt out without breaking the rules of hooks.
export default function useInViewAutoplay(ref, enabled = true, threshold = 0.4) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    // Browsers only autoplay a muted clip. React does not set `muted` when it
    // adopts pre-rendered markup at hydration, so the element can arrive
    // unmuted and every play() is refused; set it here, where it matters.
    el.muted = true;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled, threshold]);
}
